import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Truck, Home, Briefcase, User, ArrowRight, Star, ChevronRight, Lock, Zap, CheckCircle2, Phone, Mail, Building, Activity, Award } from 'lucide-react';
import { SimonsCommercial } from './SimonsCommercial';
import { SimonsLandlord } from './SimonsLandlord';
import { SimonsTrucking } from './SimonsTrucking';
import { SimonsPersonal } from './SimonsPersonal';
import { SimonsPortal } from './SimonsPortal';
import { SimonsHeader, SimonsFooter } from '../components/SimonsNavigation';
interface SimonsAcquahAgencyProps {
  onBack?: () => void;
}
export function SimonsAcquahAgency({
  onBack
}: SimonsAcquahAgencyProps) {
  const [email, setEmail] = useState('');
  const [activePage, setActivePage] = useState<'home' | 'commercial' | 'landlord' | 'trucking' | 'personal' | 'portal' | 'quote' | 'claims' | 'blog' | 'privacy' | 'contact' | 'about'>('home');
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [activePage]);
  if (activePage === 'commercial') return <SimonsCommercial onBack={() => setActivePage('home')} setActivePage={setActivePage} data-id="element-1706" />;
  if (activePage === 'landlord') return <SimonsLandlord onBack={() => setActivePage('home')} setActivePage={setActivePage} data-id="element-1707" />;
  if (activePage === 'trucking') return <SimonsTrucking onBack={() => setActivePage('home')} setActivePage={setActivePage} data-id="element-1708" />;
  if (activePage === 'personal') return <SimonsPersonal onBack={() => setActivePage('home')} setActivePage={setActivePage} data-id="element-1709" />;
  if (activePage === 'portal') return <SimonsPortal onBack={() => setActivePage('home')} setActivePage={setActivePage} data-id="element-1710" />;
  if (activePage === 'quote') return <QuotePage setActivePage={setActivePage} data-id="element-1711" />;
  if (activePage === 'claims') return <ClaimsPage setActivePage={setActivePage} data-id="element-1712" />;
  if (activePage === 'blog') return <BlogPage setActivePage={setActivePage} data-id="element-1713" />;
  if (activePage === 'privacy') return <PrivacyPage setActivePage={setActivePage} data-id="element-1714" />;
  if (activePage === 'contact') return <ContactPage setActivePage={setActivePage} data-id="element-1715" />;
  if (activePage === 'about') return <AboutPage setActivePage={setActivePage} data-id="element-1716" />;
  const services = [{
    id: 'landlord',
    title: 'Landlord Insurance',
    icon: Home,
    desc: 'Protect your rental investments with comprehensive property coverage.'
  }, {
    id: 'trucking',
    title: 'Trucking & Commercial',
    icon: Truck,
    desc: 'Specialized liability and cargo insurance for the logistics industry.'
  }, {
    id: 'business',
    title: 'Small Business',
    icon: Briefcase,
    desc: 'Tailored BOP and professional liability for growing enterprises.'
  }, {
    id: 'personal',
    title: 'Personal Bundles',
    icon: User,
    desc: 'Save strategically by bundling home, auto, and life protection.'
  }];
  return <div className="min-h-screen bg-white text-[#002366] font-sans selection:bg-[#FFD700] selection:text-[#002366]" data-id="element-1717">
      <SimonsHeader activePage={activePage} setActivePage={setActivePage} data-id="element-1718" />

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 px-6 overflow-hidden bg-gradient-to-b from-slate-50 to-white" data-id="element-1719">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1720">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-1721">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-full text-[#002366] text-[10px] font-black uppercase tracking-[0.2em] mb-8" data-id="element-1722">
              <Star size={12} className="text-[#FFD700]" fill="currentColor" data-id="element-1723" />
              Elite InsurTech Platform
            </div>
            <h1 className="text-5xl lg:text-7xl font-black leading-[1.1] mb-8 tracking-tight" data-id="element-1724">
              Protect Smarter.
              <br data-id="element-1725" />
              <span className="text-[#DC143C]" data-id="element-1726">Save Strategically.</span>
            </h1>
            <p className="text-xl text-slate-600 max-w-xl mb-10 leading-relaxed font-medium" data-id="element-1727">
              Real expertise meets modern technology. We provide strategic
              insurance solutions for high-stakes industries and personal wealth
              protection.
            </p>
            <div className="flex flex-wrap gap-4" data-id="element-1728">
              <button onClick={() => setActivePage('quote')} className="px-8 py-4 bg-[#002366] text-white rounded-xl font-bold text-lg hover:bg-[#001a4d] transition-all shadow-xl shadow-[#002366]/20 flex items-center gap-2" data-id="element-1729">
                Start My Audit <ArrowRight size={20} data-id="element-1730" />
              </button>
              <div className="flex items-center gap-3 px-6 py-4 bg-white border border-slate-200 rounded-xl" data-id="element-1731">
                <div className="flex -space-x-2" data-id="element-1732">
                  {[1, 2, 3].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-slate-200 overflow-hidden" data-id="element-1733">
                      <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User" data-id="element-1734" />
                    </div>)}
                </div>
                <div className="text-xs font-bold" data-id="element-1735">
                  <div className="flex text-[#FFD700] mb-0.5" data-id="element-1736">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} size={10} fill="currentColor" data-id="element-1737" />)}
                  </div>
                  <span className="text-slate-400" data-id="element-1738">4.8/5 from 2k+ Clients</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-1739">
            <div className="bg-white rounded-[32px] shadow-[0_32px_64px_-12px_rgba(0,35,102,0.15)] border border-slate-100 p-8 relative z-10" data-id="element-1740">
              <h3 className="text-2xl font-black mb-6 flex items-center gap-3" data-id="element-1741">
                <Zap className="text-[#FFD700]" fill="currentColor" size={24} data-id="element-1742" />
                AI-Intake Engine
              </h3>
              <p className="text-sm text-slate-500 mb-8 font-medium" data-id="element-1743">
                Select your primary coverage area to begin our strategic risk
                assessment.
              </p>

              <div className="grid grid-cols-2 gap-4" data-id="element-1744">
                {services.map(s => <button key={s.id} onClick={() => setActivePage(s.id as any)} className="p-6 bg-slate-50 border border-slate-100 rounded-2xl hover:border-[#FFD700] hover:bg-white hover:shadow-xl transition-all group text-left" data-id="element-1745">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-[#002366] mb-4 shadow-sm group-hover:text-[#FFD700] transition-colors" data-id="element-1746">
                      <s.icon size={24} data-id="element-1747" />
                    </div>
                    <span className="text-sm font-black block mb-1" data-id="element-1748">
                      {s.title.split(' ')[0]}
                    </span>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-1749">
                      Explore →
                    </span>
                  </button>)}
              </div>

              <div className="mt-8 pt-8 border-t border-slate-100" data-id="element-1750">
                <div className="flex items-center justify-between text-xs font-bold text-slate-400 uppercase tracking-widest mb-4" data-id="element-1751">
                  <span data-id="element-1752">Carrier Partners</span>
                  <span className="text-[#002366]" data-id="element-1753">Live Rates</span>
                </div>
                <div className="flex justify-between items-center text-slate-400 font-black text-sm uppercase tracking-widest" data-id="element-1754">
                  <span data-id="element-1755">Travelers</span>
                  <span data-id="element-1756">Chubb</span>
                  <span data-id="element-1757">Hartford</span>
                </div>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#FFD700]/10 rounded-full blur-2xl" data-id="element-1758"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-[#DC143C]/5 rounded-full blur-3xl" data-id="element-1759"></div>
          </motion.div>
        </div>
      </section>

      {/* Service Bento Grid */}
      <section className="py-24 px-6 bg-[#002366]" data-id="element-1760">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="max-w-7xl mx-auto" data-id="element-1761">
          <div className="text-center mb-16" data-id="element-1762">
            <h2 className="text-4xl font-black mb-4 text-white" data-id="element-1763">
              Strategic Coverage Funnels
            </h2>
            <p className="text-[#FFD700] font-bold uppercase tracking-[0.2em] text-sm" data-id="element-1764">
              Elite Protection for Every Asset
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1765">
            {services.map((s, i) => <motion.div key={i} whileHover={{
            y: -8
          }} onClick={() => setActivePage(s.id as any)} className="p-8 bg-white/5 border border-white/10 rounded-[16px] shadow-sm hover:bg-white/10 transition-all group cursor-pointer" data-id="element-1766">
                <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center text-[#FFD700] mb-6 group-hover:bg-[#FFD700] group-hover:text-[#002366] transition-all" data-id="element-1767">
                  <s.icon size={28} data-id="element-1768" />
                </div>
                <h3 className="text-xl font-black mb-3 text-white" data-id="element-1769">
                  {s.title}
                </h3>
                <p className="text-sm text-white/60 leading-relaxed mb-6 font-medium" data-id="element-1770">
                  {s.desc}
                </p>
                <button className="text-xs font-black uppercase tracking-widest text-[#FFD700] flex items-center gap-2 group-hover:text-white transition-colors" data-id="element-1771">
                  Get Quote <ChevronRight size={14} data-id="element-1772" />
                </button>
              </motion.div>)}
          </div>
        </motion.div>
      </section>

      {/* Why Us Section */}
      <section className="py-24 px-6 bg-slate-50" data-id="element-1773">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="max-w-7xl mx-auto" data-id="element-1774">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-1775">
            {[{
            title: 'Competitive Pricing',
            desc: 'We leverage our carrier relationships to secure the most strategic rates in the market.',
            icon: Zap
          }, {
            title: 'Problem-Solving Expertise',
            desc: 'Complex risks require strategic thinking. We specialize in hard-to-place commercial coverage.',
            icon: Shield
          }, {
            title: 'Fast Response Times',
            desc: 'In the insurance world, speed is security. Our team responds to all inquiries within 2 hours.',
            icon: Zap
          }].map((v, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-1776">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-[#FFD700] shadow-lg mb-8" data-id="element-1777">
                  <v.icon size={32} data-id="element-1778" />
                </div>
                <h3 className="text-2xl font-black mb-4" data-id="element-1779">{v.title}</h3>
                <p className="text-slate-500 font-medium leading-relaxed" data-id="element-1780">
                  {v.desc}
                </p>
              </div>)}
          </div>
        </motion.div>
      </section>

      {/* Lead Magnet Section */}
      <section className="py-24 px-6" data-id="element-1781">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="max-w-5xl mx-auto bg-[#DC143C] rounded-[32px] p-12 md:p-20 text-white relative overflow-hidden shadow-2xl shadow-[#DC143C]/30" data-id="element-1782">
          <div className="relative z-10 text-center" data-id="element-1783">
            <h2 className="text-4xl md:text-6xl font-black mb-6 tracking-tight" data-id="element-1784">
              Get Your Free Insurance Review
            </h2>
            <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto font-medium" data-id="element-1785">
              Our strategic auditors will review your current policies and find
              hidden gaps or over-payments in your coverage.
            </p>

            <form onSubmit={e => {
            e.preventDefault();
            setActivePage('quote');
          }} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto" data-id="element-1786">
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email address" className="flex-1 px-6 py-4 rounded-xl text-[#002366] font-bold focus:outline-none placeholder:text-slate-400 shadow-inner" data-id="element-1787" />
              <button className="px-8 py-4 bg-[#FFD700] text-[#002366] rounded-xl font-black uppercase tracking-widest hover:bg-white transition-all shadow-lg" data-id="element-1788">
                Start My Audit
              </button>
              {/* Hidden Source Tracking for CRM */}
              <input type="hidden" name="source" value="simons-acquah-lead-magnet" data-id="element-1789" />
            </form>
          </div>

          {/* Decorative Background */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 blur-[100px] rounded-full -mr-32 -mt-32" data-id="element-1790"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/10 blur-[100px] rounded-full -ml-32 -mb-32" data-id="element-1791"></div>
        </motion.div>
      </section>

      {/* Trust Bar */}
      <section className="py-16 border-y border-slate-100 overflow-hidden" data-id="element-1792">
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1
      }} className="max-w-7xl mx-auto px-6" data-id="element-1793">
          <div className="flex flex-wrap justify-center items-center gap-12 md:gap-24 text-slate-300 font-black text-xl md:text-2xl uppercase tracking-[0.2em] hover:text-slate-400 transition-all duration-700" data-id="element-1794">
            <span data-id="element-1795">Travelers</span>
            <span data-id="element-1796">Progressive</span>
            <span data-id="element-1797">Nationwide</span>
            <span data-id="element-1798">Liberty Mutual</span>
            <span data-id="element-1799">CNA</span>
          </div>
        </motion.div>
      </section>

      <SimonsFooter setActivePage={setActivePage} data-id="element-1800" />
    </div>;
}
// --- Inline Pages ---
function QuotePage({
  setActivePage
}: {
  setActivePage: (p: any) => void;
}) {
  return <div className="min-h-screen bg-slate-50 font-sans text-[#002366]" data-id="element-1801">
      <SimonsHeader activePage="quote" setActivePage={setActivePage} data-id="element-1802" />
      <main className="py-24 px-6 max-w-3xl mx-auto" data-id="element-1803">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white p-12 rounded-[32px] shadow-xl border border-slate-100" data-id="element-1804">
          <h1 className="text-4xl font-black mb-4 tracking-tight" data-id="element-1805">
            Request a Quote
          </h1>
          <p className="text-slate-500 mb-10 font-medium" data-id="element-1806">
            Provide your details below and our strategic advisors will contact
            you within 2 hours.
          </p>
          <form onSubmit={e => e.preventDefault()} className="space-y-6" data-id="element-1807">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1808">
              <div className="space-y-2" data-id="element-1809">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400" data-id="element-1810">
                  First Name
                </label>
                <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#002366]" placeholder="Jane" data-id="element-1811" />
              </div>
              <div className="space-y-2" data-id="element-1812">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400" data-id="element-1813">
                  Last Name
                </label>
                <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#002366]" placeholder="Doe" data-id="element-1814" />
              </div>
            </div>
            <div className="space-y-2" data-id="element-1815">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400" data-id="element-1816">
                Email Address
              </label>
              <input type="email" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#002366]" placeholder="jane@example.com" data-id="element-1817" />
            </div>
            <div className="space-y-2" data-id="element-1818">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400" data-id="element-1819">
                Insurance Type
              </label>
              <select className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#002366]" data-id="element-1820">
                <option data-id="element-1821">Commercial Auto</option>
                <option data-id="element-1822">Landlord Protection</option>
                <option data-id="element-1823">Trucking & Cargo</option>
                <option data-id="element-1824">Personal Bundle</option>
                <option data-id="element-1825">Other</option>
              </select>
            </div>
            <div className="space-y-2" data-id="element-1826">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400" data-id="element-1827">
                Additional Details
              </label>
              <textarea rows={4} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#002366]" placeholder="Tell us about your specific needs..." data-id="element-1828"></textarea>
            </div>
            <button type="submit" className="w-full py-4 bg-[#DC143C] text-white rounded-xl font-black uppercase tracking-widest hover:bg-[#B21031] transition-all shadow-lg shadow-[#DC143C]/20" data-id="element-1829">
              Submit Request
            </button>
          </form>
        </motion.div>
      </main>
      <SimonsFooter setActivePage={setActivePage} data-id="element-1830" />
    </div>;
}
function ClaimsPage({
  setActivePage
}: {
  setActivePage: (p: any) => void;
}) {
  return <div className="min-h-screen bg-slate-50 font-sans text-[#002366]" data-id="element-1831">
      <SimonsHeader activePage="claims" setActivePage={setActivePage} data-id="element-1832" />
      <main className="py-24 px-6 max-w-4xl mx-auto" data-id="element-1833">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-1834">
          <h1 className="text-5xl font-black mb-6 tracking-tight" data-id="element-1835">
            Claims Center
          </h1>
          <p className="text-xl text-slate-500 mb-12 font-medium" data-id="element-1836">
            We're here to help you get back to normal as quickly as possible.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-1837">
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100" data-id="element-1838">
              <Phone className="text-[#DC143C] mb-4" size={32} data-id="element-1839" />
              <h3 className="text-2xl font-black mb-2" data-id="element-1840">Emergency Claims</h3>
              <p className="text-slate-500 mb-6" data-id="element-1841">
                Available 24/7 for immediate assistance.
              </p>
              <p className="text-3xl font-black text-[#DC143C]" data-id="element-1842">
                1-800-555-0199
              </p>
            </div>
            <div className="bg-[#002366] text-white p-8 rounded-3xl shadow-xl" data-id="element-1843">
              <Shield className="text-[#FFD700] mb-4" size={32} data-id="element-1844" />
              <h3 className="text-2xl font-black mb-2" data-id="element-1845">File Online</h3>
              <p className="text-white/70 mb-6" data-id="element-1846">
                Submit your claim details securely online.
              </p>
              <button onClick={() => setActivePage('portal')} className="px-6 py-3 bg-[#FFD700] text-[#002366] rounded-xl font-black uppercase tracking-widest text-sm hover:bg-white transition-all" data-id="element-1847">
                Go to Portal
              </button>
            </div>
          </div>
        </motion.div>
      </main>
      <SimonsFooter setActivePage={setActivePage} data-id="element-1848" />
    </div>;
}
function BlogPage({
  setActivePage
}: {
  setActivePage: (p: any) => void;
}) {
  const posts = [{
    title: 'Navigating Commercial Auto Rates in 2026',
    date: 'May 12, 2026',
    img: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&q=80&w=800'
  }, {
    title: "The Landlord's Guide to Liability Protection",
    date: 'May 05, 2026',
    img: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=800'
  }, {
    title: 'Why Cyber Insurance is Non-Negotiable',
    date: 'Apr 28, 2026',
    img: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=800'
  }];
  return <div className="min-h-screen bg-slate-50 font-sans text-[#002366]" data-id="element-1849">
      <SimonsHeader activePage="blog" setActivePage={setActivePage} data-id="element-1850" />
      <main className="py-24 px-6 max-w-7xl mx-auto" data-id="element-1851">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-1852">
          <h1 className="text-5xl font-black mb-4 tracking-tight" data-id="element-1853">Risk Blog</h1>
          <p className="text-xl text-slate-500 mb-16 font-medium" data-id="element-1854">
            Strategic insights for modern enterprises.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1855">
            {posts.map((post, i) => <div key={i} className="bg-white rounded-3xl overflow-hidden shadow-sm border border-slate-100 hover:shadow-xl transition-all cursor-pointer group" data-id="element-1856">
                <div className="h-48 overflow-hidden" data-id="element-1857">
                  <img src={post.img} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1858" />
                </div>
                <div className="p-8" data-id="element-1859">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3" data-id="element-1860">
                    {post.date}
                  </p>
                  <h3 className="text-xl font-black mb-4 leading-tight" data-id="element-1861">
                    {post.title}
                  </h3>
                  <span className="text-sm font-bold text-[#DC143C] flex items-center gap-2" data-id="element-1862">
                    Read Article <ArrowRight size={14} data-id="element-1863" />
                  </span>
                </div>
              </div>)}
          </div>
        </motion.div>
      </main>
      <SimonsFooter setActivePage={setActivePage} data-id="element-1864" />
    </div>;
}
function PrivacyPage({
  setActivePage
}: {
  setActivePage: (p: any) => void;
}) {
  return <div className="min-h-screen bg-white font-sans text-[#002366]" data-id="element-1865">
      <SimonsHeader activePage="privacy" setActivePage={setActivePage} data-id="element-1866" />
      <main className="py-24 px-6 max-w-3xl mx-auto prose prose-slate prose-lg" data-id="element-1867">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} data-id="element-1868">
          <h1 className="text-4xl font-black mb-8 text-[#002366]" data-id="element-1869">
            Privacy Policy
          </h1>
          <p className="text-slate-500 font-bold" data-id="element-1870">Last Updated: May 19, 2026</p>
          <p data-id="element-1871">
            At Simons Acquah Agency, we take your privacy seriously. This policy
            outlines how we collect, use, and protect your personal and business
            information.
          </p>
          <h3 className="text-2xl font-black text-[#002366] mt-8" data-id="element-1872">
            1. Information Collection
          </h3>
          <p data-id="element-1873">
            We collect information necessary to provide accurate insurance
            quotes and maintain your policies, including contact details,
            business operations data, and claims history.
          </p>
          <h3 className="text-2xl font-black text-[#002366] mt-8" data-id="element-1874">
            2. Data Security
          </h3>
          <p data-id="element-1875">
            We employ enterprise-grade encryption and strict access controls to
            ensure your data remains secure. We do not sell your information to
            third parties.
          </p>
        </motion.div>
      </main>
      <SimonsFooter setActivePage={setActivePage} data-id="element-1876" />
    </div>;
}
function ContactPage({
  setActivePage
}: {
  setActivePage: (p: any) => void;
}) {
  return <div className="min-h-screen bg-slate-50 font-sans text-[#002366]" data-id="element-1877">
      <SimonsHeader activePage="contact" setActivePage={setActivePage} data-id="element-1878" />
      <main className="py-24 px-6 max-w-5xl mx-auto" data-id="element-1879">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="grid grid-cols-1 md:grid-cols-2 gap-16" data-id="element-1880">
          <div data-id="element-1881">
            <h1 className="text-5xl font-black mb-6 tracking-tight" data-id="element-1882">
              Get in Touch
            </h1>
            <p className="text-lg text-slate-500 mb-12 font-medium" data-id="element-1883">
              Our strategic advisors are ready to assist you with your insurance
              needs.
            </p>
            <div className="space-y-8" data-id="element-1884">
              <div className="flex items-start gap-4" data-id="element-1885">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-[#DC143C] shadow-sm" data-id="element-1886">
                  <Phone size={24} data-id="element-1887" />
                </div>
                <div data-id="element-1888">
                  <h4 className="font-black text-lg" data-id="element-1889">Phone</h4>
                  <p className="text-slate-500" data-id="element-1890">(555) 123-4567</p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-1891">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-[#DC143C] shadow-sm" data-id="element-1892">
                  <Mail size={24} data-id="element-1893" />
                </div>
                <div data-id="element-1894">
                  <h4 className="font-black text-lg" data-id="element-1895">Email</h4>
                  <p className="text-slate-500" data-id="element-1896">info@simonsacquah.com</p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white p-10 rounded-[32px] shadow-xl border border-slate-100" data-id="element-1897">
            <form onSubmit={e => e.preventDefault()} className="space-y-6" data-id="element-1898">
              <div className="space-y-2" data-id="element-1899">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400" data-id="element-1900">
                  Name
                </label>
                <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#002366]" data-id="element-1901" />
              </div>
              <div className="space-y-2" data-id="element-1902">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400" data-id="element-1903">
                  Email
                </label>
                <input type="email" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#002366]" data-id="element-1904" />
              </div>
              <div className="space-y-2" data-id="element-1905">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400" data-id="element-1906">
                  Message
                </label>
                <textarea rows={4} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#002366]" data-id="element-1907"></textarea>
              </div>
              <button type="submit" className="w-full py-4 bg-[#002366] text-white rounded-xl font-black uppercase tracking-widest hover:bg-[#001a4d] transition-all" data-id="element-1908">
                Send Message
              </button>
            </form>
          </div>
        </motion.div>
      </main>
      <SimonsFooter setActivePage={setActivePage} data-id="element-1909" />
    </div>;
}
function AboutPage({
  setActivePage
}: {
  setActivePage: (p: any) => void;
}) {
  return <div className="min-h-screen bg-white font-sans text-[#002366]" data-id="element-1910">
      <SimonsHeader activePage="about" setActivePage={setActivePage} data-id="element-1911" />
      <main className="py-24 px-6 max-w-4xl mx-auto text-center" data-id="element-1912">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-1913">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-full text-[#002366] text-[10px] font-black uppercase tracking-[0.2em] mb-8" data-id="element-1914">
            <Star size={12} className="text-[#FFD700]" fill="currentColor" data-id="element-1915" />{' '}
            Established 2024
          </div>
          <h1 className="text-5xl md:text-7xl font-black mb-8 tracking-tight" data-id="element-1916">
            Redefining Risk Management.
          </h1>
          <p className="text-xl text-slate-600 leading-relaxed font-medium mb-12" data-id="element-1917">
            Simons Acquah Agency was founded on a simple principle: modern
            enterprises need modern protection. We combine decades of
            underwriting expertise with cutting-edge technology to deliver
            strategic insurance solutions that actually drive business growth.
          </p>
          <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=1200" alt="Office" className="w-full h-96 object-cover rounded-[32px] shadow-2xl mb-16" data-id="element-1918" />
          <button onClick={() => setActivePage('quote')} className="px-10 py-5 bg-[#DC143C] text-white rounded-xl font-black text-lg hover:bg-[#B21031] transition-all shadow-xl shadow-[#DC143C]/20" data-id="element-1919">
            Work With Us
          </button>
        </motion.div>
      </main>
      <SimonsFooter setActivePage={setActivePage} data-id="element-1920" />
    </div>;
}